import * as React from 'react';
import { Loader2 } from 'lucide-react';
import type {
  EnterpriseDetail,
  EnterpriseEnumValue,
  EnterpriseSettingsUpdateInput,
} from '@proptr/shared';
import { Card } from '../../components/ui/card.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import { Textarea } from '../../components/ui/textarea.js';
import { client } from '../../main.js';
import { cn } from '../../lib/utils.js';
import { useAgencyContext } from '../AgencyDetailPage.js';

// Option lists mirror legacy enterprise.js ~lines 2280-2670 verbatim.
const STATUS_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'Active', value: 'active' },
  { id: 2, name: 'Disabled', value: 'disabled' },
];

const MARKET_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'UAE', value: 'ae' },
  { id: 2, name: 'Sweden', value: 'se' },
  { id: 3, name: 'UK', value: 'gb' },
];

const ADBOOSTER_TYPE_OPTIONS: EnterpriseEnumValue[] = [
  {
    id: 1,
    name: 'Property Booster',
    value: 'propertybooster',
    image:
      'https://res.cloudinary.com/propertybooster/image/upload/v1643467035/xsvt4uklb3evikmdhxca.png',
  },
  {
    id: 2,
    name: 'Yieldlock',
    value: 'yieldlock',
    image:
      'https://res.cloudinary.com/propertybooster/image/upload/v1643467021/w5tbkagflubbiqb0oilq.png',
  },
  {
    id: 3,
    name: 'Boosta Bostad',
    value: 'boostabostad',
    image:
      'https://res.cloudinary.com/propertybooster/image/upload/v1643466967/aauqgg4ldqwtktxt5tdt.png',
  },
];

const APPEND_UTM_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: 'Enabled', value: 'true' },
  { id: 2, name: 'Disabled', value: 'false' },
];

const START_YEAR_OPTIONS: EnterpriseEnumValue[] = Array.from(
  { length: 12 },
  (_, i) => {
    const year = 2021 + i;
    return { id: i + 1, name: String(year), value: String(year) };
  },
);

const START_MONTH_OPTIONS: EnterpriseEnumValue[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((name, i) => ({
  id: i + 1,
  name,
  value: String(i + 1).padStart(2, '0'),
}));

const CHANNELS_HEALTHCHECK_OPTIONS: EnterpriseEnumValue[] = [
  { id: 1, name: '1', value: '1' },
  { id: 2, name: '2', value: '2' },
  { id: 3, name: '3', value: '3' },
  { id: 4, name: '4', value: '4' },
];

// Browser-supported list of IANA tz names (~600 zones in modern browsers).
// Falls back to a sensible short list if the API isn't available.
function loadTimezones(): EnterpriseEnumValue[] {
  try {
    const list = (
      Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    if (Array.isArray(list) && list.length) {
      return list.map((tz, i) => ({ id: i + 1, name: tz, value: tz }));
    }
  } catch {
    /* fall through */
  }
  return [
    'UTC',
    'Europe/London',
    'Europe/Stockholm',
    'Asia/Dubai',
    'America/New_York',
    'America/Los_Angeles',
  ].map((tz, i) => ({ id: i + 1, name: tz, value: tz }));
}

export function AgencySettingsView() {
  const { enterpriseId, agency, setAgency } = useAgencyContext();
  const [saving, setSaving] = React.useState<Set<string>>(() => new Set());
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const timezones = React.useMemo(loadTimezones, []);

  // NOTE: every field-binding call below runs `useState` + `useEffect`
  // internally. They MUST be called unconditionally on every render — so
  // the `if (!agency) return …` guard lives at the bottom of the function,
  // after all hook calls. Otherwise React explodes with "rendered fewer
  // hooks than during the previous render" and the page goes blank.
  async function patch<K extends keyof EnterpriseSettingsUpdateInput>(
    key: K,
    value: EnterpriseSettingsUpdateInput[K],
  ) {
    setSaveError(null);
    setSaving((prev) => {
      const next = new Set(prev);
      next.add(String(key));
      return next;
    });
    setAgency((prev) =>
      prev ? ({ ...prev, [key]: value } as EnterpriseDetail) : prev,
    );
    try {
      await client.admin.updateEnterprise(enterpriseId, {
        [key]: value,
      } as EnterpriseSettingsUpdateInput);
    } catch (err) {
      setSaveError((err as Error).message);
      try {
        const fresh = await client.admin.getEnterprise(enterpriseId);
        setAgency(fresh);
      } catch {
        /* noop */
      }
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(String(key));
        return next;
      });
    }
  }

  // Generic helper: bind a string field to onBlur-save semantics. Local
  // state mirrors the input so React doesn't fight the user mid-typing,
  // and only commits if the value actually changed.
  function useStringField(
    key: keyof EnterpriseDetail & keyof EnterpriseSettingsUpdateInput,
  ): {
    value: string;
    onChange: (v: string) => void;
    onBlur: () => void;
  } {
    const remote = (agency?.[key] as string | null | undefined) ?? '';
    const [local, setLocal] = React.useState<string>(remote);
    // Keep local in sync if remote changes (e.g. revert after a save error).
    React.useEffect(() => {
      setLocal(remote);
    }, [remote]);
    return {
      value: local,
      onChange: setLocal,
      onBlur: () => {
        const trimmed = local.trim();
        const next = trimmed === '' ? null : trimmed;
        if (next !== (agency?.[key] ?? null)) {
          void patch(
            key,
            next as unknown as EnterpriseSettingsUpdateInput[typeof key],
          );
        }
      },
    };
  }

  // Number field — empty input → null, otherwise parse.
  function useNumberField(
    key: keyof EnterpriseDetail & keyof EnterpriseSettingsUpdateInput,
  ): {
    value: string;
    onChange: (v: string) => void;
    onBlur: () => void;
  } {
    const remote = agency?.[key];
    const remoteStr =
      typeof remote === 'number' ? String(remote) : remote == null ? '' : '';
    const [local, setLocal] = React.useState<string>(remoteStr);
    React.useEffect(() => {
      setLocal(remoteStr);
    }, [remoteStr]);
    return {
      value: local,
      onChange: setLocal,
      onBlur: () => {
        const trimmed = local.trim();
        let next: number | null;
        if (trimmed === '') {
          next = null;
        } else {
          const n = Number(trimmed);
          next = Number.isFinite(n) ? n : null;
        }
        if (next !== (agency?.[key] ?? null)) {
          void patch(
            key,
            next as unknown as EnterpriseSettingsUpdateInput[typeof key],
          );
        }
      },
    };
  }

  // Helper for enum-shaped fields — picks via the `value` discriminator and
  // writes the full enum object (or null) back.
  function selectEnumValue(
    key: keyof EnterpriseDetail & keyof EnterpriseSettingsUpdateInput,
    options: EnterpriseEnumValue[],
    selectedValue: string,
  ) {
    const opt = options.find((o) => o.value === selectedValue);
    if (!opt) return;
    void patch(
      key,
      opt as unknown as EnterpriseSettingsUpdateInput[typeof key],
    );
  }

  // Field bindings
  const name = useStringField('name');
  const companyName = useStringField('companyName');
  const parserId = useStringField('parserId');
  const email = useStringField('email');
  const companyWebsite = useStringField('companyWebsite');
  const companyBio = useStringField('companyBio');
  const adboosterId = useStringField('adboosterId');
  const primaryColor = useStringField('primaryColor');
  const secondaryColor = useStringField('secondaryColor');
  const facebookPixelIdLight = useStringField('facebookPixelIdLight');
  const facebookConversionPixelIdLight = useStringField(
    'facebookConversionPixelIdLight',
  );
  const logo = useStringField('logo');
  const favicon = useStringField('favicon');
  const themeImage = useStringField('themeImage');
  const premiumSale = useNumberField('premiumSalesPriceHealthcheck');
  const premiumRental = useNumberField('premiumRentalPriceHealthcheck');
  const minActiveListings = useNumberField('minimumActiveListingsHealthcheck');
  const minSpend = useNumberField('minimumListingSpendHealthcheck');
  const adSpend = useNumberField('advertisementSpend');

  // All hooks have run — safe to early-return now.
  if (!agency) {
    return (
      <Card className="p-6">
        <div className="font-mono text-[12px] text-muted-foreground">
          Loading…
        </div>
      </Card>
    );
  }

  const isLegacy = agency.legacy === true;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="space-y-6">
          <SectionHeading title="Enterprise" />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <Field label="Name" saving={saving.has('name')}>
              <Input {...name} onChange={(e) => name.onChange(e.target.value)} />
            </Field>
            <Field label="Company name" saving={saving.has('companyName')}>
              <Input
                {...companyName}
                onChange={(e) => companyName.onChange(e.target.value)}
              />
            </Field>
            <Field label="Parser ID" saving={saving.has('parserId')}>
              <Input
                {...parserId}
                onChange={(e) => parserId.onChange(e.target.value)}
              />
            </Field>
            <Field label="Company email" saving={saving.has('email')}>
              <Input
                {...email}
                type="email"
                onChange={(e) => email.onChange(e.target.value)}
              />
            </Field>
            <Field label="Company website" saving={saving.has('companyWebsite')}>
              <Input
                {...companyWebsite}
                type="url"
                onChange={(e) => companyWebsite.onChange(e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {isLegacy && (
              <Field
                label="Append UTM website URL"
                saving={saving.has('autoAppendWebsiteUtm')}
              >
                <Select<string>
                  options={APPEND_UTM_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.name,
                  }))}
                  value={agency.autoAppendWebsiteUtm?.value ?? 'true'}
                  onChange={(v) =>
                    selectEnumValue(
                      'autoAppendWebsiteUtm',
                      APPEND_UTM_OPTIONS,
                      v,
                    )
                  }
                />
              </Field>
            )}
            {isLegacy && (
              <Field
                label="Company bio"
                saving={saving.has('companyBio')}
                className="md:col-span-2"
              >
                <Textarea
                  rows={4}
                  maxLength={150}
                  value={companyBio.value}
                  onChange={(e) => companyBio.onChange(e.target.value)}
                  onBlur={companyBio.onBlur}
                />
              </Field>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Status" saving={saving.has('status')}>
              <Select<string>
                options={STATUS_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.name,
                }))}
                value={agency.status?.value ?? 'active'}
                onChange={(v) =>
                  selectEnumValue('status', STATUS_OPTIONS, v)
                }
              />
            </Field>
            <Field label="Market" saving={saving.has('market')}>
              <Select<string>
                options={MARKET_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.name,
                }))}
                value={agency.market?.value ?? ''}
                placeholder="Select market"
                onChange={(v) =>
                  selectEnumValue('market', MARKET_OPTIONS, v)
                }
              />
            </Field>
            <Field label="Timezone" saving={saving.has('timeZone')}>
              <Select<string>
                options={timezones.map((tz) => ({
                  value: tz.value,
                  label: tz.name,
                }))}
                value={agency.timeZone?.value ?? ''}
                placeholder="Select zone"
                onChange={(v) =>
                  selectEnumValue('timeZone', timezones, v)
                }
              />
            </Field>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-6">
          <SectionHeading title="Details" />

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="adBooster ID" saving={saving.has('adboosterId')}>
              <Input
                {...adboosterId}
                onChange={(e) => adboosterId.onChange(e.target.value)}
              />
            </Field>
            <Field label="adBooster type" saving={saving.has('adboosterType')}>
              <Select<string>
                options={ADBOOSTER_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.name,
                }))}
                value={agency.adboosterType?.value ?? ''}
                placeholder="Select type"
                onChange={(v) =>
                  selectEnumValue(
                    'adboosterType',
                    ADBOOSTER_TYPE_OPTIONS,
                    v,
                  )
                }
              />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label="Primary color"
                  saving={saving.has('primaryColor')}
                >
                  <ColorInput
                    value={primaryColor.value}
                    onChange={primaryColor.onChange}
                    onBlur={primaryColor.onBlur}
                  />
                </Field>
                <Field
                  label="Secondary color"
                  saving={saving.has('secondaryColor')}
                >
                  <ColorInput
                    value={secondaryColor.value}
                    onChange={secondaryColor.onChange}
                    onBlur={secondaryColor.onBlur}
                  />
                </Field>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  label="Start year"
                  saving={saving.has('startYearBenchmark')}
                >
                  <Select<string>
                    options={START_YEAR_OPTIONS.map((o) => ({
                      value: o.value,
                      label: o.name,
                    }))}
                    value={agency.startYearBenchmark?.value ?? ''}
                    placeholder="Select year"
                    onChange={(v) =>
                      selectEnumValue(
                        'startYearBenchmark',
                        START_YEAR_OPTIONS,
                        v,
                      )
                    }
                  />
                </Field>
                <Field
                  label="Start month"
                  saving={saving.has('startMonthBenchmark')}
                >
                  <Select<string>
                    options={START_MONTH_OPTIONS.map((o) => ({
                      value: o.value,
                      label: o.name,
                    }))}
                    value={agency.startMonthBenchmark?.value ?? ''}
                    placeholder="Select month"
                    onChange={(v) =>
                      selectEnumValue(
                        'startMonthBenchmark',
                        START_MONTH_OPTIONS,
                        v,
                      )
                    }
                  />
                </Field>
              </div>
              <Field
                label="Premium SALE listings threshold"
                sublabel="(healthcheck)"
                saving={saving.has('premiumSalesPriceHealthcheck')}
              >
                <Input
                  type="number"
                  value={premiumSale.value}
                  onChange={(e) => premiumSale.onChange(e.target.value)}
                  onBlur={premiumSale.onBlur}
                />
              </Field>
              <Field
                label="Premium RENTAL listings threshold"
                sublabel="(healthcheck)"
                saving={saving.has('premiumRentalPriceHealthcheck')}
              >
                <Input
                  type="number"
                  value={premiumRental.value}
                  onChange={(e) => premiumRental.onChange(e.target.value)}
                  onBlur={premiumRental.onBlur}
                />
              </Field>
              <Field
                label="Minimum active listings"
                sublabel="(healthcheck)"
                saving={saving.has('minimumActiveListingsHealthcheck')}
              >
                <Input
                  type="number"
                  value={minActiveListings.value}
                  onChange={(e) => minActiveListings.onChange(e.target.value)}
                  onBlur={minActiveListings.onBlur}
                />
              </Field>
              <Field
                label="Minimum spend per listing"
                sublabel="(healthcheck)"
                saving={saving.has('minimumListingSpendHealthcheck')}
              >
                <Input
                  type="number"
                  value={minSpend.value}
                  onChange={(e) => minSpend.onChange(e.target.value)}
                  onBlur={minSpend.onBlur}
                />
              </Field>
              <Field
                label="Ad networks"
                sublabel="(healthcheck)"
                saving={saving.has('channelsHealthcheck')}
              >
                <Select<string>
                  options={CHANNELS_HEALTHCHECK_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.name,
                  }))}
                  value={agency.channelsHealthcheck?.value ?? '1'}
                  onChange={(v) =>
                    selectEnumValue(
                      'channelsHealthcheck',
                      CHANNELS_HEALTHCHECK_OPTIONS,
                      v,
                    )
                  }
                />
              </Field>
            </div>

            <div className="space-y-4">
              {isLegacy && (
                <Field
                  label="Meta Pixel ID"
                  sublabel="(scale)"
                  saving={saving.has('facebookPixelIdLight')}
                >
                  <Input
                    {...facebookPixelIdLight}
                    onChange={(e) =>
                      facebookPixelIdLight.onChange(e.target.value)
                    }
                  />
                </Field>
              )}
              {isLegacy && (
                <Field
                  label="Meta Pixel Conversions API Token"
                  sublabel="(needed for CRM events)"
                  saving={saving.has('facebookConversionPixelIdLight')}
                >
                  <Input
                    {...facebookConversionPixelIdLight}
                    onChange={(e) =>
                      facebookConversionPixelIdLight.onChange(e.target.value)
                    }
                  />
                </Field>
              )}
              <Field
                label="Advertisement spend"
                sublabel="(monthly)"
                saving={saving.has('advertisementSpend')}
              >
                <Input
                  type="number"
                  value={adSpend.value}
                  onChange={(e) => adSpend.onChange(e.target.value)}
                  onBlur={adSpend.onBlur}
                />
              </Field>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-6">
          <SectionHeading title="Brand assets" />
          <p className="font-sans text-[13px] text-muted-foreground">
            Paste a hosted image URL for now. Direct upload coming soon.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <BrandAssetField
              label="Logo"
              urlField={logo}
              saving={saving.has('logo')}
              kind="logo"
            />
            <BrandAssetField
              label="Banner image"
              urlField={themeImage}
              saving={saving.has('themeImage')}
              kind="banner"
            />
            <BrandAssetField
              label="Favicon"
              urlField={favicon}
              saving={saving.has('favicon')}
              kind="favicon"
            />
          </div>
        </div>
      </Card>

      {saveError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-[11px] text-destructive">
          {saveError}
        </div>
      )}
    </div>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
      {title}
    </h2>
  );
}

function Field({
  label,
  sublabel,
  saving,
  children,
  className,
}: {
  label: string;
  sublabel?: string;
  saving?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center gap-2">
        <Label className="font-sans text-[13px] font-medium normal-case tracking-normal text-foreground">
          {label}
          {sublabel && (
            <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              {sublabel}
            </span>
          )}
        </Label>
        {saving && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        )}
      </div>
      {children}
    </div>
  );
}

function ColorInput({
  value,
  onChange,
  onBlur,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}) {
  // Show a swatch on the left only when the value parses as a hex color.
  const swatch = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)
    ? value
    : null;
  return (
    <div className="relative">
      {swatch && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 rounded border border-border"
          style={{ backgroundColor: swatch }}
        />
      )}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder="#000000"
        className={cn(swatch && 'pl-10')}
      />
    </div>
  );
}

function BrandAssetField({
  label,
  urlField,
  saving,
  kind,
}: {
  label: string;
  urlField: { value: string; onChange: (v: string) => void; onBlur: () => void };
  saving?: boolean;
  kind: 'logo' | 'banner' | 'favicon';
}) {
  // Favicon's source asset is tiny (16-64px), so cap the rendered size so it
  // doesn't blur up to fill the same h-32 box that the logo/banner use.
  const imgClass =
    kind === 'favicon'
      ? 'max-h-12 max-w-12 object-contain'
      : 'max-h-full max-w-full object-contain';
  return (
    <Field label={label} saving={saving}>
      <div className="space-y-2">
        <div className="flex h-32 items-center justify-center overflow-hidden rounded-md border border-dashed border-border bg-card dark:bg-card-raised">
          {urlField.value ? (
            <img src={urlField.value} alt={label} className={imgClass} />
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              No {label.toLowerCase()}
            </span>
          )}
        </div>
        <Input
          value={urlField.value}
          onChange={(e) => urlField.onChange(e.target.value)}
          onBlur={urlField.onBlur}
          placeholder="https://…"
        />
      </div>
    </Field>
  );
}
